import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import CheckIcon from '@material-ui/icons/Check'
import CloseIcon from '@material-ui/icons/Close'
import LinkIcon from '@material-ui/icons/Link'
import LocalShippingIcon from '@material-ui/icons/LocalShipping'
import BlockIcon from '@material-ui/icons/Block'
import NavigateBeforeIcon from '@material-ui/icons/NavigateBefore'
import NavigateNextIcon from '@material-ui/icons/NavigateNext'
import axios from 'axios'
import {
  PRICING_MODES,
  MAPPING_STATUSES,
  PAGE_SIZE,
  formatPrice,
  relativeDate,
  PricingModeChip,
  AvailabilityChip
} from './offeringHelpers'

const useStyles = makeStyles((theme) => ({
  filterBar: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    alignItems: 'flex-end'
  },
  filterField: {
    minWidth: 140
  },
  searchField: {
    minWidth: 200
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1.5)
  },
  tableContainer: {
    overflowX: 'auto'
  },
  cellCompact: {
    padding: theme.spacing(0.75, 1.5),
    fontSize: '0.8rem'
  },
  headerCell: {
    padding: theme.spacing(0.75, 1.5),
    fontWeight: 700,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap'
  },
  actionsCell: {
    padding: theme.spacing(0.5, 1),
    whiteSpace: 'nowrap'
  },
  chipRetail: { backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  chipWholesale: { backgroundColor: '#f3e5f5', color: '#6a1b9a', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  chipPitPickup: { backgroundColor: '#fff3e0', color: '#e65100', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  chipYardPickup: { backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  chipDelivered: { backgroundColor: '#fce4ec', color: '#880e4f', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  chipUnknown: { backgroundColor: '#f5f5f5', color: '#757575', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  stockIn: { backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  stockOut: { backgroundColor: '#ffebee', color: '#c62828', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  stockUnknown: { backgroundColor: '#f5f5f5', color: '#9e9e9e', fontWeight: 600, fontSize: '0.68rem', height: 20 },
  mappedIcon: { color: '#2e7d32', fontSize: 18, verticalAlign: 'middle' },
  unmappedIcon: { color: '#bdbdbd', fontSize: 18, verticalAlign: 'middle' },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5)
  },
  paginationText: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: 'center'
  }
}))

export default function FlatOfferingsTable ({ vendors, onOpenMap, onIgnore, onOpenCreate, onOpenQuickCalc }) {
  const classes = useStyles()

  // ── Filter state ─────────────────────────────────────────────────────────────
  const [filterVendor, setFilterVendor] = useState('')
  const [filterLocation, setFilterLocation] = useState('')
  const [filterGradation, setFilterGradation] = useState('')
  const [filterPricingMode, setFilterPricingMode] = useState('')
  const [filterMappingStatus, setFilterMappingStatus] = useState('')
  const [filterSearch, setFilterSearch] = useState('')

  // ── Remote filter options ────────────────────────────────────────────────────
  const [locations, setLocations] = useState([])
  const [gradations, setGradations] = useState([])

  // ── Table state ──────────────────────────────────────────────────────────────
  const [offerings, setOfferings] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(0)
  const [loading, setLoading] = useState(true)

  // ── Load filter options once ─────────────────────────────────────────────────
  useEffect(() => {
    axios.get('/api/aggregate-dashboard/locations?limit=200')
      .then((res) => setLocations(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('[FlatOfferingsTable] locations fetch error:', err))

    axios.get('/api/aggregate-dashboard/gradations')
      .then((res) => setGradations(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error('[FlatOfferingsTable] gradations fetch error:', err))
  }, [])

  // ── Fetch offerings ──────────────────────────────────────────────────────────
  const fetchOfferings = useCallback(() => {
    setLoading(true)
    const params = {
      limit: PAGE_SIZE,
      skip: page * PAGE_SIZE
    }
    if (filterVendor) params.vendorId = filterVendor
    if (filterLocation) params.locationId = filterLocation
    if (filterGradation) params.gradationId = filterGradation
    if (filterPricingMode) params.pricingMode = filterPricingMode
    if (filterMappingStatus) params.mappingStatus = filterMappingStatus
    if (filterSearch.trim()) params.search = filterSearch.trim()

    axios.get('/api/aggregate-dashboard/offerings', { params })
      .then((res) => {
        if (Array.isArray(res.data)) {
          setOfferings(res.data)
          setTotal(res.data.length < PAGE_SIZE ? page * PAGE_SIZE + res.data.length : (page + 2) * PAGE_SIZE)
        } else {
          setOfferings(res.data.offerings || [])
          setTotal(res.data.total || 0)
        }
      })
      .catch((err) => console.error('[FlatOfferingsTable] fetch error:', err))
      .finally(() => setLoading(false))
  }, [page, filterVendor, filterLocation, filterGradation, filterPricingMode, filterMappingStatus, filterSearch])

  useEffect(() => {
    fetchOfferings()
  }, [fetchOfferings])

  // ── Filter change helpers ────────────────────────────────────────────────────
  const handleFilterChange = (setter) => (e) => {
    setter(e.target.value)
    setPage(0)
  }

  const handleReset = () => {
    setFilterVendor('')
    setFilterLocation('')
    setFilterGradation('')
    setFilterPricingMode('')
    setFilterMappingStatus('')
    setFilterSearch('')
    setPage(0)
  }

  // ── Ignore handler ───────────────────────────────────────────────────────────
  const handleIgnore = async (offering) => {
    try {
      await onIgnore(offering)
      fetchOfferings()
    } catch (err) {
      console.error('[FlatOfferingsTable] ignore error:', err)
    }
  }

  // ── Pagination ───────────────────────────────────────────────────────────────
  const startRow = page * PAGE_SIZE + 1
  const endRow = page * PAGE_SIZE + offerings.length
  const hasPrev = page > 0
  const hasNext = offerings.length === PAGE_SIZE

  return (
    <>
      {/* Filter bar */}
      <div className={classes.filterBar}>
        <TextField
          select
          label="Vendor"
          value={filterVendor}
          onChange={handleFilterChange(setFilterVendor)}
          size="small"
          variant="outlined"
          className={classes.filterField}
        >
          <MenuItem value="">All vendors</MenuItem>
          {vendors.map((v) => (
            <MenuItem key={v._id} value={v._id}>{v.name}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Location"
          value={filterLocation}
          onChange={handleFilterChange(setFilterLocation)}
          size="small"
          variant="outlined"
          className={classes.filterField}
        >
          <MenuItem value="">All locations</MenuItem>
          {locations.map((loc) => (
            <MenuItem key={loc._id} value={loc._id}>
              {loc.name}{loc.vendorId && loc.vendorId.name ? ` (${loc.vendorId.name})` : ''}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Gradation"
          value={filterGradation}
          onChange={handleFilterChange(setFilterGradation)}
          size="small"
          variant="outlined"
          className={classes.filterField}
        >
          <MenuItem value="">All gradations</MenuItem>
          {gradations.map((g) => (
            <MenuItem key={g._id} value={g._id}>{g.displayName}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Pricing Mode"
          value={filterPricingMode}
          onChange={handleFilterChange(setFilterPricingMode)}
          size="small"
          variant="outlined"
          className={classes.filterField}
        >
          <MenuItem value="">All modes</MenuItem>
          {PRICING_MODES.map((m) => (
            <MenuItem key={m} value={m}>{m}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          label="Mapping Status"
          value={filterMappingStatus}
          onChange={handleFilterChange(setFilterMappingStatus)}
          size="small"
          variant="outlined"
          className={classes.filterField}
        >
          <MenuItem value="">All statuses</MenuItem>
          {MAPPING_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>{s}</MenuItem>
          ))}
        </TextField>

        <TextField
          label="Search"
          value={filterSearch}
          onChange={handleFilterChange(setFilterSearch)}
          size="small"
          variant="outlined"
          className={classes.searchField}
          placeholder="Product title..."
        />

        <Button size="small" onClick={handleReset} variant="text">
          Reset
        </Button>
      </div>

      {/* Table header row */}
      <div className={classes.tableHeader}>
        <Typography variant="subtitle2" color="textSecondary">
          {loading ? 'Loading\u2026' : `${offerings.length === 0 ? 'No' : `${startRow}\u2013${endRow}`} offering${offerings.length !== 1 ? 's' : ''}`}
        </Typography>
        <Box display="flex" style={{ gap: 8 }}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<LocalShippingIcon />}
            onClick={onOpenQuickCalc}
          >
            Quick Calculator
          </Button>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<AddIcon />}
            onClick={onOpenCreate}
          >
            Add Offering
          </Button>
        </Box>
      </div>

      {/* Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" pt={6}>
          <CircularProgress size={32} />
        </Box>
      ) : offerings.length === 0 ? (
        <Paper variant="outlined">
          <div className={classes.emptyState}>
            <Typography variant="body2" color="textSecondary">
              No offerings found matching the current filters.
            </Typography>
          </div>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined" className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.headerCell}>Supplier</TableCell>
                <TableCell className={classes.headerCell}>Location</TableCell>
                <TableCell className={classes.headerCell}>Material</TableCell>
                <TableCell className={classes.headerCell}>Gradation</TableCell>
                <TableCell className={classes.headerCell}>Mode</TableCell>
                <TableCell className={classes.headerCell}>Price</TableCell>
                <TableCell className={classes.headerCell}>Availability</TableCell>
                <TableCell className={classes.headerCell}>Mapped?</TableCell>
                <TableCell className={classes.headerCell}>Last Seen</TableCell>
                <TableCell className={classes.headerCell}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {offerings.map((o) => (
                <TableRow key={o._id} hover>
                  <TableCell className={classes.cellCompact}>
                    {(o.vendorId && o.vendorId.name) ? o.vendorId.name : '\u2014'}
                  </TableCell>
                  <TableCell className={classes.cellCompact}>
                    {(o.supplierLocationId && o.supplierLocationId.name) ? o.supplierLocationId.name : '\u2014'}
                  </TableCell>
                  <TableCell className={classes.cellCompact}>
                    {(o.materialProfileId && o.materialProfileId.displayName)
                      ? o.materialProfileId.displayName
                      : (o.productTitle || '\u2014')}
                  </TableCell>
                  <TableCell className={classes.cellCompact}>
                    {(o.gradationId && o.gradationId.displayName)
                      ? o.gradationId.displayName
                      : (o.variantTitle || '\u2014')}
                  </TableCell>
                  <TableCell className={classes.cellCompact}>
                    <PricingModeChip mode={o.pricingMode} classes={classes} />
                  </TableCell>
                  <TableCell className={classes.cellCompact} style={{ whiteSpace: 'nowrap' }}>
                    {formatPrice(o.price, o.unit)}
                  </TableCell>
                  <TableCell className={classes.cellCompact}>
                    <AvailabilityChip status={o.availabilityStatus} classes={classes} />
                  </TableCell>
                  <TableCell className={classes.cellCompact} style={{ textAlign: 'center' }}>
                    {o.aspireCatalogItemId
                      ? <CheckIcon className={classes.mappedIcon} titleAccess="Mapped" />
                      : <CloseIcon className={classes.unmappedIcon} titleAccess="Not mapped" />}
                  </TableCell>
                  <TableCell className={classes.cellCompact} style={{ whiteSpace: 'nowrap' }}>
                    {relativeDate(o.lastSeenAt)}
                  </TableCell>
                  <TableCell className={classes.actionsCell}>
                    <Tooltip title="Map to Aspire catalog item">
                      <IconButton
                        size="small"
                        onClick={() => onOpenMap(o)}
                        style={{ color: o.aspireCatalogItemId ? '#1565c0' : '#666' }}
                      >
                        <LinkIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {o.mappingStatus !== 'IGNORED' && (
                      <Tooltip title="Mark as Ignored">
                        <IconButton
                          size="small"
                          onClick={() => handleIgnore(o)}
                          style={{ color: '#9e9e9e' }}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Pagination */}
      {!loading && offerings.length > 0 && (
        <div className={classes.pagination}>
          <Typography className={classes.paginationText}>
            {startRow}\u2013{endRow}
            {total > 0 && ` of ${total}`}
          </Typography>
          <IconButton size="small" onClick={() => setPage((p) => p - 1)} disabled={!hasPrev}>
            <NavigateBeforeIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => setPage((p) => p + 1)} disabled={!hasNext}>
            <NavigateNextIcon fontSize="small" />
          </IconButton>
        </div>
      )}
    </>
  )
}
